import { AppHubCard } from "@/components/common/AppHubCard";
import { AppHubPage } from "@/components/common/AppHubPage";
import { useNav } from "@/utils/nav";
import { createFileRoute } from "@tanstack/react-router";
import { ClipboardCheck, FileText, FormInput, Shapes } from "lucide-react";

const designItems = [
    {
        title: "Components",
        description: "Preview shared form components, auth screens, dictionaries, and interaction patterns.",
        icon: <FormInput className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-violet-500 to-violet-600",
        bgGradient: "bg-gradient-to-br from-violet-50 to-violet-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-violet-100/50 before:to-violet-200/50",
        borderColor: "border-violet-200 hover:border-violet-300",
        primaryButtonClassName: "bg-violet-600 hover:bg-violet-700",
        links: [
            { to: "/design/components/auth", label: "Auth screens" },
            { to: "/design/components/buttons", label: "Buttons" },
            { to: "/design/components/inputs", label: "Form inputs" },
            { to: "/design/components/dictionaries", label: "Dictionaries" },
            { to: "/design/components/input-switch", label: "Input switch" },
            { to: "/design/components/entry-values", label: "Entry values" },
        ],
    },
    {
        title: "Forms",
        description:
            "Annotated questionnaire screens that name every variable, such as form caption, instructions, page label, input caption and blocks, and show where each one appears.",
        icon: <FileText className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-sky-500 to-sky-600",
        bgGradient: "bg-gradient-to-br from-sky-50 to-sky-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-sky-100/50 before:to-sky-200/50",
        borderColor: "border-sky-200 hover:border-sky-300",
        primaryButtonClassName: "bg-sky-600 hover:bg-sky-700",
        links: [{ to: "/design/forms", label: "Form screens" }],
    },
    {
        title: "Icons",
        description: "Animated Lottie icons, diary state illustrations, and rating or pain faces.",
        icon: <Shapes className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-fuchsia-500 to-fuchsia-600",
        bgGradient: "bg-gradient-to-br from-fuchsia-50 to-fuchsia-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-fuchsia-100/50 before:to-fuchsia-200/50",
        borderColor: "border-fuchsia-200 hover:border-fuchsia-300",
        primaryButtonClassName: "bg-fuchsia-600 hover:bg-fuchsia-700",
        links: [
            { to: "/design/icons/animations", label: "Animations" },
            { to: "/design/icons/states", label: "States" },
            { to: "/design/icons/faces", label: "Faces" },
        ],
    },
    {
        title: "Features",
        description:
            "Preview product flows, including multi-step onboarding, study clinician invites, worklist automations, build custom reminder messages, and inspect the schedule used to send reminders and other messages.",
        icon: <ClipboardCheck className="w-7 h-7 text-white" />,
        iconBg: "bg-gradient-to-br from-indigo-500 to-indigo-600",
        bgGradient: "bg-gradient-to-br from-indigo-50 to-indigo-50",
        hoverOverlay: "before:bg-gradient-to-br before:from-indigo-100/50 before:to-indigo-200/50",
        borderColor: "border-indigo-200 hover:border-indigo-300",
        primaryButtonClassName: "bg-indigo-600 hover:bg-indigo-700",
        links: [
            { to: "/design/features/otivio-frictionless-onboarding", label: "Multi-step onboarding" },
            { to: "/design/features/manager-study-clinician-invite-plan", label: "Study clinician invites" },
            { to: "/design/features/worklist", label: "Worklist" },
            { to: "/design/features/worklist/automations", label: "Automations" },
            { to: "/design/features/reminder-sequences", label: "Reminder sequences" },
            { to: "/design/features/messaging-schedule", label: "Messaging schedule" },
        ],
    },
];

const DesignPage = () => {
    useNav([{ label: "Design", route: { to: "/design" } }]);

    return (
        <AppHubPage
            title="Design"
            description="Component gallery and product-flow previews for building Patient Watch."
        >
            {designItems.map((item) => (
                <AppHubCard key={item.title} {...item} />
            ))}
        </AppHubPage>
    );
};

export const Route = createFileRoute("/_app/design/")({
    component: DesignPage,
});
